import React, { useState } from "react"
import { Information } from "../../simulation/types"
import {
  SearchTrigger,
  TreeNodeMapping,
  TutorialConfig,
  TutorialMessage,
} from "../types/tutorial-config"
import TreeNodePicker from "./tree-node-picker"
import VariableTextInput from "./variable-text-input"
import { GlobalsTab } from "./step-list"

type Props = {
  tab: GlobalsTab
  config: TutorialConfig
  categories: Information[]
  onChange: (patch: Partial<TutorialConfig>) => void
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")

const GlobalsPanel: React.FC<Props> = ({ tab, config, categories, onChange }) => {
  if (tab === "variables") return <VariablesTab config={config} onChange={onChange} />
  if (tab === "messages") return <MessagesTab config={config} onChange={onChange} />
  if (tab === "search-triggers")
    return <SearchTriggersTab config={config} onChange={onChange} />
  return <TreeMappingsTab config={config} categories={categories} onChange={onChange} />
}

export default GlobalsPanel

const VariablesTab: React.FC<{
  config: TutorialConfig
  onChange: (patch: Partial<TutorialConfig>) => void
}> = ({ config, onChange }) => {
  const entries = Object.entries(config.variables ?? {})
  const update = (next: Record<string, string>) => onChange({ variables: next })
  return (
    <div className="p-3 space-y-2 text-sm">
      <div className="font-semibold">Variables</div>
      <p className="text-xs text-gray-600">
        Substituted into step text, attach selectors, and message descriptions via{" "}
        <code>{"{{name}}"}</code>.
      </p>
      {entries.length === 0 && (
        <div className="text-xs text-gray-500 italic">No variables yet.</div>
      )}
      {entries.map(([name, value]) => (
        <div key={name} className="flex gap-2 items-center">
          <input
            value={name}
            onChange={(e) => {
              const next: Record<string, string> = {}
              for (const [k, v] of entries) {
                next[k === name ? e.target.value : k] = v
              }
              update(next)
            }}
            className="border rounded px-2 py-1 font-mono w-1/3"
          />
          <input
            value={value}
            onChange={(e) => update({ ...config.variables, [name]: e.target.value })}
            className="border rounded px-2 py-1 flex-1"
          />
          <button
            type="button"
            onClick={() => {
              const next = { ...config.variables }
              delete next[name]
              update(next)
            }}
            className="text-red-600 text-xs">
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => {
          let n = "newVar"
          let i = 1
          while (config.variables?.[n]) {
            n = `newVar${i++}`
          }
          update({ ...config.variables, [n]: "" })
        }}
        className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">
        + Add variable
      </button>
    </div>
  )
}

const MessagesTab: React.FC<{
  config: TutorialConfig
  onChange: (patch: Partial<TutorialConfig>) => void
}> = ({ config, onChange }) => {
  const variableNames = [
    ...Object.keys(config.variables ?? {}),
    ...config.treeNodeMappings.map((m) => m.name),
  ]
  const update = (i: number, patch: Partial<TutorialMessage>) => {
    onChange({
      messages: config.messages.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
    })
  }
  const remove = (i: number) =>
    onChange({ messages: config.messages.filter((_, idx) => idx !== i) })
  const add = () =>
    onChange({
      messages: [
        ...config.messages,
        {
          id: `msg-${config.messages.length + 1}`,
          injectAfterStep: config.steps[0]?.id ?? "",
          description: "",
          type: "I",
        },
      ],
    })
  return (
    <div className="p-3 space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <div className="font-semibold">Messages</div>
        <button
          type="button"
          onClick={add}
          className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">
          + Add message
        </button>
      </div>
      {config.messages.length === 0 && (
        <div className="text-xs text-gray-500 italic">No messages yet.</div>
      )}
      {config.messages.map((m, i) => (
        <div key={i} className="border rounded p-2 bg-gray-50">
          <div className="grid grid-cols-3 gap-2 mb-2">
            <label className="flex flex-col text-xs">
              ID
              <input
                value={m.id}
                onChange={(e) => update(i, { id: e.target.value })}
                className="border rounded px-2 py-1 font-mono"
              />
            </label>
            <label className="flex flex-col text-xs">
              Type
              <select
                value={m.type}
                onChange={(e) =>
                  update(i, { type: e.target.value as TutorialMessage["type"] })
                }
                className="border rounded px-2 py-1">
                <option value="N">N — News</option>
                <option value="D">D — Decision</option>
                <option value="P">P — Plan</option>
                <option value="I">I — Information</option>
              </select>
            </label>
            <label className="flex flex-col text-xs">
              Inject after step
              <select
                value={m.injectAfterStep}
                onChange={(e) => update(i, { injectAfterStep: e.target.value })}
                className="border rounded px-2 py-1">
                <option value="">(pick step)</option>
                {config.steps.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.id}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="flex flex-col text-xs">
            Description
            <VariableTextInput
              value={m.description}
              onChange={(v) =>
                update(i, {
                  description: Array.isArray(v) ? v.join("\n\n") : v,
                })
              }
              variableNames={variableNames}
            />
          </label>
          <label className="flex flex-col text-xs mt-2">
            Validate response (optional)
            <input
              value={m.validateResponse ?? ""}
              onChange={(e) =>
                update(i, { validateResponse: e.target.value || undefined })
              }
              className="border rounded px-2 py-1"
            />
          </label>
          <div className="text-right mt-1">
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-xs text-red-600">
              Delete message
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

const SearchTriggersTab: React.FC<{
  config: TutorialConfig
  onChange: (patch: Partial<TutorialConfig>) => void
}> = ({ config, onChange }) => {
  const update = (i: number, patch: Partial<SearchTrigger>) =>
    onChange({
      searchTriggers: config.searchTriggers.map((t, idx) =>
        idx === i ? { ...t, ...patch } : t,
      ),
    })
  const remove = (i: number) =>
    onChange({ searchTriggers: config.searchTriggers.filter((_, idx) => idx !== i) })
  const add = () =>
    onChange({
      searchTriggers: [...config.searchTriggers, { searchPattern: "", eventName: "" }],
    })
  return (
    <div className="p-3 space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <div className="font-semibold">Search triggers</div>
        <button
          type="button"
          onClick={add}
          className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">
          + Add trigger
        </button>
      </div>
      {config.searchTriggers.length === 0 && (
        <div className="text-xs text-gray-500 italic">No triggers yet.</div>
      )}
      {config.searchTriggers.map((t, i) => (
        <div key={i} className="grid grid-cols-2 gap-2 items-end">
          <label className="flex flex-col text-xs">
            Search pattern (substring)
            <input
              value={t.searchPattern}
              onChange={(e) => {
                const pattern = e.target.value
                const auto = `customSearch_${slug(pattern)}`
                update(i, {
                  searchPattern: pattern,
                  eventName: t.eventName || auto,
                })
              }}
              className="border rounded px-2 py-1"
            />
          </label>
          <div className="flex gap-1">
            <label className="flex flex-col text-xs flex-1">
              Event name
              <input
                value={t.eventName}
                onChange={(e) => update(i, { eventName: e.target.value })}
                className="border rounded px-2 py-1 font-mono"
              />
            </label>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-xs text-red-600 self-end pb-1">
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

const TreeMappingsTab: React.FC<{
  config: TutorialConfig
  categories: Information[]
  onChange: (patch: Partial<TutorialConfig>) => void
}> = ({ config, categories, onChange }) => {
  const [pickingFor, setPickingFor] = useState<number | null>(null)
  const update = (i: number, patch: Partial<TreeNodeMapping>) =>
    onChange({
      treeNodeMappings: config.treeNodeMappings.map((m, idx) =>
        idx === i ? { ...m, ...patch } : m,
      ),
    })
  const remove = (i: number) =>
    onChange({
      treeNodeMappings: config.treeNodeMappings.filter((_, idx) => idx !== i),
    })
  const add = () =>
    onChange({
      treeNodeMappings: [
        ...config.treeNodeMappings,
        { name: `node${config.treeNodeMappings.length + 1}`, nodePath: [] },
      ],
    })
  return (
    <div className="p-3 space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <div className="font-semibold">Tree mappings</div>
        <button
          type="button"
          onClick={add}
          className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">
          + Add mapping
        </button>
      </div>
      {config.treeNodeMappings.length === 0 && (
        <div className="text-xs text-gray-500 italic">
          No mappings. Add one to reference tree nodes by name in selectors.
        </div>
      )}
      {config.treeNodeMappings.map((m, i) => (
        <div key={i} className="border rounded p-2 bg-gray-50">
          <div className="grid grid-cols-2 gap-2 mb-1">
            <label className="flex flex-col text-xs">
              Name
              <input
                value={m.name}
                onChange={(e) => update(i, { name: e.target.value })}
                className="border rounded px-2 py-1 font-mono"
              />
            </label>
            <label className="flex flex-col text-xs">
              Suffix (optional)
              <input
                value={m.suffix ?? ""}
                onChange={(e) =>
                  update(i, { suffix: e.target.value || undefined })
                }
                placeholder="-container"
                className="border rounded px-2 py-1 font-mono"
              />
            </label>
          </div>
          <div className="text-xs">
            <span className="font-medium">Path: </span>
            {m.nodePath.length > 0 ? m.nodePath.join(" → ") : <em>(unset)</em>}
            <button
              type="button"
              onClick={() => setPickingFor(pickingFor === i ? null : i)}
              className="ml-2 text-indigo-600">
              {pickingFor === i ? "Close" : "Pick from tree"}
            </button>
            <button
              type="button"
              onClick={() => remove(i)}
              className="ml-2 text-red-600">
              Delete
            </button>
          </div>
          {pickingFor === i && (
            <div className="mt-2">
              <TreeNodePicker
                categories={categories}
                initialPath={m.nodePath}
                onSelect={(path) => {
                  update(i, { nodePath: path })
                  setPickingFor(null)
                }}
                onCancel={() => setPickingFor(null)}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
